import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useCompanyOptions, useDepartmentOptions, useTeamOptions } from '../../hooks/useOrgQueries';
import { useGenerateReport } from '../../hooks/useReportQueries';
import { LedgerDialog, LedgerField, LedgerInput, LedgerSelect, LedgerSelectOption } from '../ui';

type OrgLevel = 'company' | 'team' | 'department';

const ORG_LEVELS: OrgLevel[] = ['company', 'team', 'department'];

const levelOptions: LedgerSelectOption[] = [
  { value: 'company', label: 'Company' },
  { value: 'team', label: 'Team' },
  { value: 'department', label: 'Department' },
];

const levelPlural: Record<OrgLevel, string> = {
  company: 'companies',
  team: 'teams',
  department: 'departments',
};

const VALID_ATTACHMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'png', 'jpg', 'jpeg', 'gif', 'zip'];
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10MB

const isoDate = (date: Date): string => date.toISOString().split('T')[0];

const defaultDateFrom = (): string => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return isoDate(date);
};

const defaultDateTo = (): string => isoDate(new Date());

const schema = yup.object({
  organizationType: yup.string().oneOf(ORG_LEVELS).defined().default('company'),
  organizationId: yup.string().defined().default('').required('Organization is required'),
  dateFrom: yup.string().defined().default('').required('Start date is required'),
  dateTo: yup
    .string()
    .defined()
    .default('')
    .required('End date is required')
    .test('date-range', 'Start date must be before end date', function (value) {
      const { dateFrom } = this.parent as { dateFrom?: string };
      if (!dateFrom || !value) return true;
      return dateFrom <= value;
    }),
  notes: yup.string().defined().default(''),
});

type ReportGeneratorFormValues = yup.InferType<typeof schema>;

const buildDefaultValues = (): ReportGeneratorFormValues => ({
  organizationType: 'company',
  organizationId: '',
  dateFrom: defaultDateFrom(),
  dateTo: defaultDateTo(),
  notes: '',
});

interface ReportGeneratorDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerated: (reportId: string) => void;
}

export const ReportGeneratorDialog: React.FC<ReportGeneratorDialogProps> = ({ open, onClose, onGenerated }) => {
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const orgTypeTriggerRef = useRef<HTMLButtonElement>(null);

  const generateReport = useGenerateReport();
  const generating = generateReport.isPending;

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ReportGeneratorFormValues>({
    resolver: yupResolver(schema),
    defaultValues: buildDefaultValues(),
    mode: 'onTouched',
  });

  const level = watch('organizationType') as OrgLevel;
  const notes = watch('notes');

  // Each level's option list fetches only while the dialog is open AND that
  // level is the active one, so switching levels lazy-loads on demand.
  const companiesQuery = useCompanyOptions(open && level === 'company');
  const teamsQuery = useTeamOptions(open && level === 'team');
  const departmentsQuery = useDepartmentOptions(open && level === 'department');

  const optionsPending =
    (level === 'company' && companiesQuery.isPending) ||
    (level === 'team' && teamsQuery.isPending) ||
    (level === 'department' && departmentsQuery.isPending);
  const optionsError =
    (level === 'company' && companiesQuery.isError) ||
    (level === 'team' && teamsQuery.isError) ||
    (level === 'department' && departmentsQuery.isError);

  const activeData = level === 'company' ? companiesQuery.data : level === 'team' ? teamsQuery.data : departmentsQuery.data;

  const orgOptions: LedgerSelectOption[] = [
    { value: '', label: 'Select an organization' },
    ...(level === 'company'
      ? (companiesQuery.data ?? []).map((company) => ({ value: company.id, label: company.name }))
      : level === 'team'
        ? (teamsQuery.data ?? []).map((team) => ({ value: team.id, label: `${team.name} (${team.companyName})` }))
        : (departmentsQuery.data ?? []).map((department) => ({
            value: department.id,
            label: `${department.name} (${department.teamName})`,
          }))),
  ];

  // Reseed a fresh form (and clear staged attachments/errors) every time the
  // dialog opens, so a prior run's values never leak into the next one.
  useEffect(() => {
    if (open) {
      reset(buildDefaultValues());
      setError(null);
      setAttachments([]);
    }
  }, [open, reset]);

  // Auto-select the organization when a level's options resolve to exactly
  // one entry — mirrors the original single-org convenience behavior.
  useEffect(() => {
    if (activeData && activeData.length === 1) {
      setValue('organizationId', activeData[0].id, { shouldValidate: true });
    }
  }, [activeData, setValue]);

  const handleClose = () => {
    if (generating) return;
    reset(buildDefaultValues());
    setError(null);
    setAttachments([]);
    onClose();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    const validFiles = files.filter((file) => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!extension || !VALID_ATTACHMENT_EXTENSIONS.includes(extension)) {
        setError(`File type .${extension} is not allowed`);
        return false;
      }
      if (file.size > MAX_ATTACHMENT_SIZE) {
        setError(`File ${file.name} exceeds 10MB limit`);
        return false;
      }
      return true;
    });

    setAttachments((prev) => [...prev, ...validFiles]);
    event.target.value = '';
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (values: ReportGeneratorFormValues) => {
    setError(null);
    try {
      const reportId = await generateReport.mutateAsync({
        request: {
          organization_type: values.organizationType as OrgLevel,
          organization_id: values.organizationId,
          date_from: values.dateFrom,
          date_to: values.dateTo,
          notes: values.notes,
        },
        attachments,
      });
      onGenerated(reportId);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    }
  };

  const noOrgsHint =
    !optionsPending && (activeData?.length ?? 0) === 0
      ? `No ${levelPlural[level]} found. Please create one first.`
      : undefined;

  return (
    <LedgerDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title="Generate Risk Assessment Report"
      size="md"
      preventClose={generating}
      initialFocus={orgTypeTriggerRef}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={generating}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="report-generator-form"
            disabled={generating || isSubmitting || optionsPending}
            className="cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:cursor-default disabled:opacity-60"
          >
            {generating ? 'Generating…' : 'Generate Report'}
          </button>
        </>
      }
    >
      <form id="report-generator-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {(error || optionsError) && (
          <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
            {error ?? 'Failed to load organizations'}
          </p>
        )}

        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <LedgerField label="Organization Type">
            <Controller
              name="organizationType"
              control={control}
              render={({ field }) => (
                <LedgerSelect
                  ariaLabel="Organization Type"
                  value={field.value}
                  options={levelOptions}
                  disabled={generating}
                  onValueChange={(next) => {
                    // A level switch invalidates the previous selection — it
                    // belongs to another org collection entirely.
                    if (next !== field.value) setValue('organizationId', '');
                    field.onChange(next);
                  }}
                  triggerRef={orgTypeTriggerRef}
                />
              )}
            />
          </LedgerField>

          <LedgerField label="Organization" error={errors.organizationId?.message} hint={noOrgsHint}>
            <Controller
              name="organizationId"
              control={control}
              render={({ field }) => (
                <LedgerSelect
                  ariaLabel="Organization"
                  value={field.value}
                  options={orgOptions}
                  onValueChange={field.onChange}
                  disabled={optionsPending || generating}
                />
              )}
            />
          </LedgerField>

          <LedgerField label="Start Date" htmlFor="report-date-from" error={errors.dateFrom?.message}>
            <LedgerInput
              id="report-date-from"
              type="date"
              required
              invalid={!!errors.dateFrom}
              disabled={generating}
              {...register('dateFrom')}
            />
          </LedgerField>

          <LedgerField label="End Date" htmlFor="report-date-to" error={errors.dateTo?.message}>
            <LedgerInput
              id="report-date-to"
              type="date"
              required
              invalid={!!errors.dateTo}
              disabled={generating}
              {...register('dateTo')}
            />
          </LedgerField>
        </div>

        <LedgerField label="Notes (Optional)" htmlFor="report-notes">
          <textarea
            id="report-notes"
            rows={4}
            disabled={generating}
            placeholder="Add any additional context or specific requirements for the report..."
            className="w-full border border-rule-faint bg-transparent p-2 text-scale-1 text-ink outline-none focus:border-accent"
            {...register('notes')}
          />
        </LedgerField>

        <LedgerField
          label="Attachments (Optional)"
          htmlFor="report-attachments"
          hint="Allowed: PDF, Word, Excel, Text, Images (PNG, JPG, GIF), ZIP. Max 10MB per file."
        >
          <input
            id="report-attachments"
            type="file"
            multiple
            disabled={generating}
            onChange={handleFileSelect}
            accept={VALID_ATTACHMENT_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
            className="block w-full cursor-pointer text-scale-1 text-ink file:mr-3 file:cursor-pointer file:border file:border-ink file:bg-transparent file:px-3 file:py-1.5 file:text-scale-1 file:text-ink hover:file:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          />
          {attachments.length > 0 && (
            <ul className="mt-2 space-y-1">
              {attachments.map((file, index) => (
                <li key={index} className="flex items-center justify-between gap-2 border-b border-rule-faint py-1">
                  <span className="min-w-0 flex-1 truncate text-scale-1 text-ink" title={file.name}>
                    {file.name}{' '}
                    <span className="font-mono text-scale-0 text-ink-muted">
                      ({(file.size / 1024 / 1024).toFixed(2)}MB)
                    </span>
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => handleRemoveAttachment(index)}
                    disabled={generating}
                    className="cap-label shrink-0 cursor-pointer hover:!text-ink disabled:cursor-default disabled:opacity-60"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </LedgerField>

        <p className="text-scale-1 text-ink-muted">
          The AI will analyze all risks created within the selected date range and generate a comprehensive
          executive-level overview.
          {notes && ' Your notes will be taken into consideration during report generation.'}
          {attachments.length > 0 && ' Attached files will be stored with the report for reference.'}
        </p>
      </form>
    </LedgerDialog>
  );
};
